import { sumBy } from 'lodash';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { HtmlTable } from '../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { formatCurrency } from '../../../CommonUtils/formatNumber';
import {
  selectManagedAccountGroups,
  selectUnManagedAccountGroups,
} from '../../../ContextualSidebar/NetWorthSidebar/NetWorthSidebarSelector';
import { StyledWrapper } from './MobileAccountsList.styles';
import Icon from '../../../CommonComponents/Icon';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { isApexAccountOpen } from '../../../Apex/ApexHelpers';
import { useIsEmbeddedMobile } from '../../../_hooks/useIsEmbeddedMobile';
import { ModalHeader } from '../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalBody } from '../../../CommonComponents/OPL/Modal/ModalBody';
import { Modal } from '../../../CommonComponents/OPL/Modal/Modal';
import { TabSlug } from '../../types';

interface PortfolioGroups {
  selectable: string | null;
  name: string | null;
  nameId: string | null;
}

interface Account {
  ApexStatus: string;
  IsApexOrion: boolean;
  Item: {
    AccountId: string;
    AccountTypeCategory: string | null;
    NonSpecificPositionsValue: number;
    Description: string | null;
    InstitutionName: string | null;
  };
  ManagedAccountId: string | null;
  Name: string;
  Value: number;
  isApexPendingAction: boolean;
  orionName: string;
}

type AccountGroups = Record<
  string,
  {
    accounts: Account[];
    portfolioGroup?: PortfolioGroups;
  }
>;

export const MobileAccountsList = ({ clientId, updatedAt }) => {
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);

  const managedAccountGroups = useSelector(selectManagedAccountGroups) as AccountGroups;

  const unManagedAccountGroups = useSelector(selectUnManagedAccountGroups) as AccountGroups;

  const accountGroups = { ...managedAccountGroups, ...unManagedAccountGroups };

  const isEmbeddedMobile = useIsEmbeddedMobile();

  const navigateToAccount = (account: Account) => {
    const accountId = account.isApexPendingAction
      ? account.ManagedAccountId
      : account.Item.AccountId;

    ADV.util.Router.go2('AccountPerformanceReporting', {
      clientId,
      accountId,
      tabSlug: TabSlug.Summary,
      isApex: (account.IsApexOrion && isApexAccountOpen(account.ApexStatus)) || false,
    });
  };

  const navigateToGroup = (groupId: string | null) =>
    groupId &&
    ADV.util.Router.go2('AccountPerformanceReporting', {
      clientId,
      groupId,
      tabSlug: TabSlug.Summary,
      isApex: false,
    });

  return (
    <StyledWrapper isEmbeddedMobile={isEmbeddedMobile}>
      <Text variant="cardTitle" mx="16" mb="8">
        My Accounts
      </Text>

      <Text color="neutral.500" mx="16" mb="16">
        Last updated: {updatedAt}
      </Text>

      <HtmlTable variant="embedded">
        <table style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '65%' }} />
            <col style={{ width: '35%' }} />
          </colgroup>

          {Object.entries(accountGroups).map(([key, { accounts, portfolioGroup }]) => (
            <React.Fragment key={key}>
              <thead>
                <tr
                  onClick={() => {
                    if (portfolioGroup) {
                      navigateToGroup(portfolioGroup.selectable);
                    }
                  }}
                >
                  <th>
                    <Text
                      variant="smallCaps"
                      textAlign="left"
                      css="overflow: hidden; white-space: nowrap; text-overflow: ellipsis;"
                    >
                      {portfolioGroup
                        ? `${portfolioGroup.name} ${portfolioGroup.nameId}`
                        : 'Unmanaged Accounts'}
                    </Text>
                  </th>
                  <th>
                    <Text variant="smallCaps" textAlign="right">
                      {formatCurrency(sumBy(accounts, account => account.Value))}
                    </Text>
                  </th>
                </tr>
              </thead>
              <tbody>
                {accounts.map(account => (
                  <tr
                    key={`${account.Name}-${account.ManagedAccountId}`}
                    onClick={() => {
                      if (!portfolioGroup) {
                        setSelectedAccount(account);
                      } else {
                        navigateToAccount(account);
                      }
                    }}
                  >
                    <td>
                      <Text
                        textAlign="left"
                        css="overflow: hidden; white-space: nowrap; text-overflow: ellipsis;"
                      >
                        {account.Name}
                      </Text>
                      <Text textAlign="left">{account.orionName}</Text>
                    </td>
                    <td>
                      <Box
                        display="flex"
                        flexDirection="row"
                        justifyContent="flex-end"
                        alignItems="center"
                      >
                        <Text textAlign="right" mr="8">
                          {formatCurrency(account.Value)}
                        </Text>
                        <Icon className="far fa-angle-right" />
                      </Box>
                    </td>
                  </tr>
                ))}
              </tbody>
            </React.Fragment>
          ))}
        </table>
      </HtmlTable>

      {selectedAccount && (
        <Modal
          isOpen
          onRequestClose={() => {
            setSelectedAccount(null);
          }}
          variant="bottom"
        >
          <ModalHeader>{selectedAccount.Item.AccountTypeCategory}</ModalHeader>
          <ModalCloseButton
            onClick={() => {
              setSelectedAccount(null);
            }}
          />

          <ModalBody css="padding:0">
            <HtmlTable variant="embedded">
              <table>
                <thead>
                  <tr>
                    <th>
                      <Text variant="smallCaps" textAlign="left">
                        Description
                      </Text>
                    </th>
                    <th>
                      <Text variant="smallCaps" textAlign="right">
                        Value
                      </Text>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  <tr>
                    <td>Market Value</td>
                    <td>
                      <Text fontWeight={500} variant="tabular" textAlign="right">
                        {formatCurrency(selectedAccount.Item.NonSpecificPositionsValue, {
                          decimalPlaces: 2,
                        })}
                      </Text>
                    </td>
                  </tr>
                  <tr>
                    <td>Description</td>
                    <td>
                      <Text fontWeight={500} variant="tabular" textAlign="right">
                        {selectedAccount.Item.Description}
                      </Text>
                    </td>
                  </tr>
                  <tr>
                    <td>Institution</td>
                    <td>
                      <Text fontWeight={500} variant="tabular" textAlign="right">
                        {selectedAccount.Item.InstitutionName}
                      </Text>
                    </td>
                  </tr>
                </tbody>
              </table>
            </HtmlTable>
          </ModalBody>
        </Modal>
      )}
    </StyledWrapper>
  );
};
